import { ChangeDetectionStrategy, Component, computed, ElementRef, EventEmitter, input, Output, ViewChild, AfterViewInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrivacyConsentService } from '../services/privacy-consent.service';

type BannerPosition = 'top' | 'bottom';

@Component({
    selector: 'app-cookie-banner',
    templateUrl: './cookie-banner.component.html',
    styleUrls: ['./cookie-banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule]

})
export class CookieBannerComponent implements AfterViewInit, OnDestroy {
    @ViewChild('bannerElement') bannerElement!: ElementRef<HTMLElement>;
    @Output() onAccepted = new EventEmitter<void>();

    // --- Inputs מופרדים (כמו שביקשת) ---
    readonly position = input<BannerPosition>('top');      // 'top' | 'bottom'// כותרת ימין         // טקסט כפתור
    readonly forceShow = input<boolean>(false);

    readonly show = computed(() =>
        (this.forceShow() === true || this.privacyConsentService.accepted() === false)
    );

    private focusableElements: HTMLElement[] = [];
    private previouslyFocusedElement: HTMLElement | null = null;

    constructor(private privacyConsentService: PrivacyConsentService) {
        effect(() => {
            if (this.show()) {
                this.trapFocus();
                this.setPageInert(true);
            } else {
                this.restoreFocus();
                this.setPageInert(false);
            }
        });
    }

    ngAfterViewInit(): void {
        if (this.show()) {
            this.setFocus();
        }
    }

    ngOnDestroy(): void {
        this.restoreFocus();
        this.setPageInert(false);
    }

    private setPageInert(inert: boolean): void {
        setTimeout(() => {
            // חסימת כל התוכן מלבד הבאנר
            const routerOutlet = document.querySelector('router-outlet');
            const main = document.querySelector('main');
            const header = document.querySelector('header');
            const footer = document.querySelector('footer');

            [routerOutlet, main, header, footer].forEach(element => {
                if (element) {
                    if (inert) {
                        element.setAttribute('inert', '');
                        element.setAttribute('aria-hidden', 'true');
                    } else {
                        element.removeAttribute('inert');
                        element.removeAttribute('aria-hidden');
                    }
                }
            });
        }, 0);
    }

    private trapFocus(): void {
        setTimeout(() => {
            if (!this.bannerElement) return;

            // שמירת האלמנט שהיה בפוקוס
            this.previouslyFocusedElement = document.activeElement as HTMLElement;

            // מציאת כל האלמנטים הניתנים לפוקוס בבאנר
            const focusableSelectors = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
            this.focusableElements = Array.from(
                this.bannerElement.nativeElement.querySelectorAll(focusableSelectors)
            );

            // הוספת מאזין לטאב
            document.addEventListener('keydown', this.handleTabKey);

            // מיקוד בבאנר
            this.setFocus();
        }, 100);
    }

    private handleTabKey = (event: KeyboardEvent): void => {
        if (event.key !== 'Tab' || !this.show()) return;

        if (this.focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];

        if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    };

    private restoreFocus(): void {
        document.removeEventListener('keydown', this.handleTabKey);
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
    }

    onAccept(): void {
        this.privacyConsentService.accept();
        this.onAccepted.emit();
        setTimeout(() => {
            const main = document.querySelector('main') as HTMLElement;
            if (main) {
                main.setAttribute('tabindex', '-1');
                main.focus();
            }
        }, 50);
    }

    setFocus() {
        queueMicrotask(() => {
            this.bannerElement.nativeElement.focus();
        });
    }
}
